/**
 * Estrelas.ts — Fundo com PARALAXE (Fase 6): nebulosas e duas camadas de estrelas
 * que andam mais devagar que o mundo quando a camera se move, dando profundidade.
 *
 * Tudo em coordenadas de TELA, embrulhado (modulo) no viewport: quando a camera
 * anda, o que sai por um lado entra pelo outro. Fica na starsLayer (fixa na
 * tela, nao recebe o transform da camera). Estrelas proximas cintilam.
 */

import { Container, Sprite } from 'pixi.js';
import { texturaNebulosa, texturaPonto } from './Texturas';
import type { Camera } from './Camera';
import type { PixiApp } from './PixiApp';

interface Corpo {
  sprite: Sprite;
  bx: number;
  by: number;
  fase: number;      // pra cintilar
  alphaBase: number;
}

interface Camada {
  container: Container;
  paralaxe: number;
  corpos: Corpo[];
  margem: number;    // tamanho extra pra embrulhar sem "pop"
}

export class Estrelas {
  private camadas: Camada[] = [];
  private tempo = 0;

  constructor(private readonly pixi: PixiApp, private readonly camera: Camera) {}

  iniciar(): void {
    this.criar();
    this.pixi.onResize(() => this.recriar());
    this.pixi.addTicker((t) => this.atualizar(t.deltaMS));
  }

  private criar(): void {
    const vp = this.pixi.viewport;
    const ponto = texturaPonto();

    // Nebulosas: grandes, lentas, quase invisiveis (a profundidade vem delas)
    const neb: Camada = { container: new Container(), paralaxe: 0.05, corpos: [], margem: 900 };
    const matizes = [222, 275, 190];
    for (let i = 0; i < 3; i++) {
      const s = new Sprite(texturaNebulosa(matizes[i], 101 + i * 13));
      s.anchor.set(0.5);
      const tam = 700 + i * 220;
      s.width = tam; s.height = tam;
      s.alpha = 0.75;
      s.blendMode = 'add';
      neb.container.addChild(s);
      neb.corpos.push({ sprite: s, bx: Math.random() * (vp.w + 900), by: Math.random() * (vp.h + 900), fase: 0, alphaBase: 0.75 });
    }

    // Estrelas longe: muitas, pequenas, quase paradas
    const longe: Camada = { container: new Container(), paralaxe: 0.18, corpos: [], margem: 0 };
    for (let i = 0; i < 220; i++) {
      const s = new Sprite(ponto);
      s.anchor.set(0.5);
      const tam = 1.2 + Math.random() * 2.2;
      s.width = tam; s.height = tam;
      const a = 0.25 + Math.random() * 0.5;
      s.alpha = a;
      longe.container.addChild(s);
      longe.corpos.push({ sprite: s, bx: Math.random() * vp.w, by: Math.random() * vp.h, fase: Math.random() * 6.28, alphaBase: a });
    }

    // Estrelas perto: poucas, maiores, cintilam, andam mais
    const perto: Camada = { container: new Container(), paralaxe: 0.42, corpos: [], margem: 0 };
    for (let i = 0; i < 70; i++) {
      const s = new Sprite(ponto);
      s.anchor.set(0.5);
      const tam = 2.5 + Math.random() * 3.5;
      s.width = tam; s.height = tam;
      const a = 0.45 + Math.random() * 0.5;
      s.alpha = a;
      s.tint = Math.random() < 0.15 ? 0xbfdbfe : Math.random() < 0.1 ? 0xfde68a : 0xffffff;
      perto.container.addChild(s);
      perto.corpos.push({ sprite: s, bx: Math.random() * vp.w, by: Math.random() * vp.h, fase: Math.random() * 6.28, alphaBase: a });
    }

    this.camadas = [neb, longe, perto];
    for (const c of this.camadas) this.pixi.starsLayer.addChild(c.container);
  }

  private atualizar(dtMs: number): void {
    this.tempo += dtMs / 1000;
    const vp = this.pixi.viewport;
    const cx = this.camera.x;
    const cy = this.camera.y;
    for (const cam of this.camadas) {
      const W = vp.w + cam.margem;
      const H = vp.h + cam.margem;
      const dx = cx * cam.paralaxe;
      const dy = cy * cam.paralaxe;
      for (const corpo of cam.corpos) {
        corpo.sprite.x = ((((corpo.bx - dx) % W) + W) % W) - cam.margem / 2;
        corpo.sprite.y = ((((corpo.by - dy) % H) + H) % H) - cam.margem / 2;
        if (corpo.fase) {
          corpo.sprite.alpha = corpo.alphaBase * (0.75 + 0.25 * Math.sin(this.tempo * 2.1 + corpo.fase));
        }
      }
    }
  }

  private recriar(): void {
    this.pixi.starsLayer.removeChildren();
    this.criar();
  }
}
